import { mergeTierImageUrl } from "../assets/merge-tier-urls.js";
import {
  RING_BASE_SLOTS,
  RING_MAX_CHIPS,
} from "./evolution-ring-dom.js";
import {
  EVOLUTION_TIER_FADED,
  EVOLUTION_TIER_UNDISCOVERED,
  getEvolutionTierVisualState,
  isRunMaxTierChip,
} from "./evolution-tier-visual.js";

/** Base creature chips on the strip (11th appears once T-rex is unlocked). */
export var STRIP_CHIP_COUNT = RING_BASE_SLOTS;
export var STRIP_MAX_CHIPS = RING_MAX_CHIPS;

/**
 * @param {object[]} chain
 * @param {Record<number, boolean>} unlocked
 * @param {function(number, object[]): boolean} [skip]
 */
function getStripMaxNodes(chain, unlocked, skip) {
  if (!chain || chain.length === 0) return RING_BASE_SLOTS;
  var finalIndex = chain.length - 1;
  if (finalIndex <= 0) return Math.min(RING_BASE_SLOTS, chain.length);
  if (skip && skip(finalIndex, chain)) return RING_BASE_SLOTS;
  var gateTier = chain[finalIndex - 1];
  if (gateTier && unlocked && unlocked[gateTier.id]) return RING_MAX_CHIPS;
  return RING_BASE_SLOTS;
}

/**
 * True when the chip at chain index is the hidden-final "?" teaser chip.
 *
 * @param {object[]} chain
 * @param {number} chainIndex
 * @param {Record<number, boolean>} unlocked
 */
function isSecretTeaserStripChip(chain, chainIndex, unlocked) {
  if (!chain || chain.length <= RING_BASE_SLOTS) return false;
  if (chainIndex !== chain.length - 1) return false;
  var tier = chain[chainIndex];
  if (!tier) return false;
  return !(unlocked && unlocked[tier.id]);
}

/**
 * Both layouts show up to 11 chips (the "?" / Dragon chip appears once T-rex unlocks).
 *
 * @param {object[]} chain
 * @param {Record<number, boolean>} unlocked
 * @param {function(number, object[]): boolean} [skip]
 * @param {boolean} [suppressSecretChip] render as if the secret chip is not exposed yet
 * @returns {{ index: number, tier: object }[]}
 */
export function buildStripTiers(chain, unlocked, skip, suppressSecretChip) {
  var maxNodes = suppressSecretChip
    ? Math.min(RING_BASE_SLOTS, getStripMaxNodes(chain, unlocked, skip))
    : getStripMaxNodes(chain, unlocked, skip);
  var stripTiers = [];
  for (var i = 0; i < chain.length && stripTiers.length < maxNodes; i++) {
    if (skip && skip(i, chain)) continue;
    stripTiers.push({ index: i, tier: chain[i] });
  }
  return stripTiers;
}

/**
 * Standalone secret "?" chip node — appended by the reveal ceremony when the
 * strip squeezes from 10 to 11 chips before the final populate.
 *
 * @param {Document} doc
 * @param {number} slotIndex position in the grid (--evo-i)
 * @param {number} tierIndex chain index for data-tier-index
 * @returns {HTMLElement}
 */
export function createStripSecretChipNode(doc, slotIndex, tierIndex) {
  doc = doc || document;
  var node = doc.createElement("span");
  node.className = "evo-strip__node evo-strip__node--undiscovered evo-strip__node--secret";
  node.style.setProperty("--evo-i", String(slotIndex));
  node.setAttribute("data-tier-index", String(tierIndex));
  var mark = doc.createElement("span");
  mark.className = "evo-strip__node-mark";
  mark.textContent = "?";
  node.appendChild(mark);
  return node;
}

/**
 * Stable signature for evolution-strip DOM state — skip full rebuild when unchanged.
 *
 * @param {{
 *   unlockedTiers: Record<number, boolean>,
 *   chain: object[],
 *   currentTierId?: number,
 *   skipTier?: function(number, object[]): boolean,
 * }} state
 */
export function buildEvolutionStripSignature(state) {
  state = state || {};
  var chain = state.chain || [];
  var unlocked = state.unlockedTiers || {};
  var skip = state.skipTier;
  var stripTiers = buildStripTiers(chain, unlocked, skip);
  var parts = [String(stripTiers.length)];
  for (var i = 0; i < stripTiers.length; i++) {
    var tier = stripTiers[i].tier;
    parts.push(String(tier.id) + ":" + (unlocked[tier.id] ? "1" : "0"));
  }
  parts.push("cur=" + (state.currentTierId != null ? state.currentTierId : ""));
  return parts.join("|");
}

/** Unique paint-server ids — shared `evoStripBgGrad` breaks when another strip is `display:none`. */
var stripArrowGradSeq = 0;

/**
 * Horizontal arrow SVG backdrop — chevron bar with multi-stop gradient (design X.svg).
 * @returns {SVGSVGElement}
 */
function createStripArrowSvg() {
  stripArrowGradSeq += 1;
  var gradId = "evoStripBgGrad-" + stripArrowGradSeq;
  var bg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  bg.setAttribute("class", "evo-strip__bg");
  bg.setAttribute("viewBox", "0 0 1000 100");
  bg.setAttribute("preserveAspectRatio", "none");
  bg.innerHTML =
    "<defs>"
    + "<linearGradient id=\"" + gradId + "\" x1=\"0\" y1=\"50\" x2=\"982\" y2=\"50\" gradientUnits=\"userSpaceOnUse\">"
    + "<stop stop-color=\"#72D8FA\"/>"
    + "<stop offset=\"0.163462\" stop-color=\"#55C8E8\"/>"
    + "<stop offset=\"0.384615\" stop-color=\"#3ED598\"/>"
    + "<stop offset=\"0.629808\" stop-color=\"#8CC832\"/>"
    + "<stop offset=\"0.875\" stop-color=\"#F0A714\"/>"
    + "</linearGradient>"
    + "</defs>"
    + "<path d=\"M0 0H982L1000 50L982 100H0V0Z\" fill=\"url(#" + gradId + ")\"/>";
  return bg;
}

/**
 * @param {HTMLElement} mount
 * @param {{
 *   unlockedTiers: Record<number, boolean>,
 *   chain: object[],
 *   currentTierId?: number,
 *   skipTier?: function(number, object[]): boolean,
 * }} state
 */
export function renderEvolutionStrip(mount, state) {
  if (!mount) return;
  state = state || {};
  var chain = state.chain || [];
  var unlocked = state.unlockedTiers || {};
  var skip = state.skipTier;
  var stripTiers = buildStripTiers(chain, unlocked, skip, state.suppressSecretChip);

  mount.innerHTML = "";
  mount.classList.add("evo-strip-mount--ready");

  var panel = document.createElement("div");
  panel.className = "evo-strip-panel vertical-chrome-panel";
  if (stripTiers.length > RING_BASE_SLOTS) {
    panel.classList.add("evo-strip-panel--squeezed");
  }
  panel.style.setProperty("--evo-strip-count", String(stripTiers.length));

  panel.appendChild(createStripArrowSvg());

  var strip = document.createElement("div");
  strip.className = "evo-strip";
  if (stripTiers.length > RING_BASE_SLOTS) {
    strip.classList.add("evo-strip--squeezed");
  }
  strip.style.setProperty("--evo-strip-count", String(stripTiers.length));

  var nodes = document.createElement("div");
  nodes.className = "evo-strip__nodes";
  for (var n = 0; n < stripTiers.length; n++) {
    var entry = stripTiers[n];
    var node = document.createElement("span");
    node.className = "evo-strip__node";
    node.style.setProperty("--evo-i", String(n));
    node.setAttribute("data-tier-index", String(entry.index));
    if (state.pendingTierIndex != null && entry.index === state.pendingTierIndex) {
      node.classList.add("evo-strip__node--undiscovered");
      node.classList.add("evo-strip__node--reveal-pending");
    } else if (isSecretTeaserStripChip(chain, entry.index, unlocked)) {
      node.classList.add("evo-strip__node--undiscovered");
      node.classList.add("evo-strip__node--secret");
      var mark = document.createElement("span");
      mark.className = "evo-strip__node-mark";
      mark.textContent = "?";
      node.appendChild(mark);
    } else {
      var visual = getEvolutionTierVisualState(entry.tier.id, unlocked, state.currentTierId);
      if (visual === EVOLUTION_TIER_UNDISCOVERED) {
        node.classList.add("evo-strip__node--undiscovered");
      } else if (visual === EVOLUTION_TIER_FADED) {
        node.classList.add("evo-strip__node--faded");
      }
      if (isRunMaxTierChip(entry.tier.id, visual, state.currentTierId)) {
        node.classList.add("evo-strip__node--run-max");
      }
      var nimg = document.createElement("img");
      nimg.src = mergeTierImageUrl(entry.tier.assetId || "cell");
      nimg.alt = entry.tier.name || "";
      node.appendChild(nimg);
    }
    if (state.hiddenTierIndex != null && entry.index === state.hiddenTierIndex) {
      node.classList.add("evo-reveal-chip-hidden");
    }
    nodes.appendChild(node);
  }
  strip.appendChild(nodes);

  panel.appendChild(strip);
  mount.appendChild(panel);
}